'use strict';

import dragula from 'dragula';
import CustomOrder from './custom-order';
import TemplateHelper from './template-helper';
import Item from '../api/item';
import MediaItem from '../api/mediaitem';
import Compressor from 'compressorjs';

class ImageManager {
  /**
   * Constructs a new ImageManager instance
   * @param {object} [options] -
   * @param {object} [options.customOrder] - A configured CustomOrder instance
   * @param {array} [options.enabledPages] - List of selectors for pages, on which ImageManager should be active
   * @param {object} [options.item] - Item instance corresponding to the current item
   * @param {string} [options.previewsSelector] - A selector corresponding to the element in which mediaItem thumbnails reside
   * @param {object} [options.templateHelper] - TODO: use this to populate image settings modals
   * @param {string} [options.uploadInputSelector] - A selector corresponding to the file input element
   */
  constructor(options) {
    let im = this; // store a reference for customOrder callbacks
    const settings = {
      customOrder: new CustomOrder({
        updateItem: function (item) {
          let mediaItem = new MediaItem(item.pid);
          mediaItem.setWeight(item.weight);
        },
        updateAll: function (list) {
          const weights = list.map((item) => {
            return { pid: item.pid, weight: item.weight };
          });
          im.item.updateWeights(weights);
        },
      }),
      enabledPages: ['.page--edit-item-images', '.page--order', '.page--pre-storage-event'],
      item: new Item(window.ITEM_ID),
      previewsSelector: '.image-grid__container',
      templateHelper: new TemplateHelper(),
      uploadInputSelector: '.item-images__upload-form input[name="image"]',
      editImageModalTemplate: 'modal-box',
      editImageModalButtonSelector: '.image-grid__edit',
      imageCompressionSettings: {
        // https://www.npmjs.com/package/compressorjs
        maxWidth: 1500,
        maxHeight: 1500,
        quality: 0.6,
      },
      allowedImageTypes: [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/heic',
        'image/heif',
      ],
      allowedImageExtensions: [
        '.jpg',
        '.jpeg',
        '.png',
        '.gif',
        '.webp',
        '.heic',
        '.heif',
      ],
    };

    Object.assign(this, settings, options);
  }

  init() {
    if (document.querySelectorAll(this.enabledPages.join(',')).length <= 0) {
      // short circuit initialization, as we're on a page that doesn't need this module.
      return;
    }

    if (Array.isArray(window.IMAGES_DATA)) {
      this.imagesData = this.customOrder.initList(window.IMAGES_DATA);
    }

    // Initialize upload button
    this.uploadInput = document.querySelector(this.uploadInputSelector);
    if (this.uploadInput) {
      this.uploadInput.multiple = true;
      // this.uploadInput.addEventListener('change', this._handleUploadInputChange.bind(this));
      this.uploadInput.addEventListener(
        'change',
        this._compressAndUploadImages.bind(this),
      );
    }

    // Initialize list drag-n-drop
    const drake = dragula(
      Array.from(document.querySelectorAll(this.previewsSelector)),
    );

    drake.on('drop', this._handleItemDrop.bind(this));
  }

  /**
   * Determine the old and new indexes of a moved item
   * @param {HTMLElement} el - The item that was moved. A data-item-id attribute must be present!
   * @param {undefined} _ - not used
   * @param {undefined} __ - not used
   * @param {HTMLElement} sibling - The item preceding the moved item in it's new position.
   */
  _handleItemDrop(el, _, __, sibling) {
    // Find moved element's original index within images list
    const subject = this.imagesData.find(
      (item) => item.pid === parseInt(el.dataset.itemId, 10),
    );
    const fromIndex = this.imagesData.indexOf(subject);

    // Find moved element's new index within images list
    if (sibling) {
      sibling = this.imagesData.find(
        (item) => item.pid === parseInt(sibling.dataset.itemId),
      );
    }
    const toIndex = sibling
      ? this.imagesData.indexOf(sibling)
      : this.imagesData.length - 1;

    // Move item within images list
    this.imagesData = this.customOrder.moveItem(
      this.imagesData,
      fromIndex,
      toIndex,
    );
  }

  /**
   * Update file's progress
   *
   * - Adds file compression and upload progress info elements to page if not there already.
   * - Calculates and updates the progress values.
   * - Finally when all transfers are done, calls callback method from this.fileProgressIndicator object
   *
   * @param {Event}   event     - onchange event from file input
   * @param {string}  [type]    - type of progress to increment, values: 'compress' or 'transfer'
   * @param {boolean} [success] - true if no errors, else false. This may be used for telling user that errors occoured.
   */
  _updateFileProgress(event, type, success) {
    // add required markup to page if not there yet
    if (!this.fileProgressIndicator.parentEl) {
      const markup = this.templateHelper.render(
        'multi-image-upload-processing',
        {},
        document.body,
        'none',
      );
      event.target.parentNode.insertAdjacentHTML('afterEnd', markup);
      this.fileProgressIndicator.parentEl = document.getElementById(
        'multi-image-upload-processing',
      );
      this.fileProgressIndicator.compEl = document.getElementById(
        'multi-image-upload-processing__comp-progress',
      );
      this.fileProgressIndicator.sendEl = document.getElementById(
        'multi-image-upload-processing__send-progress',
      );
      event.target.parentNode.style.display = 'none'; // hide button to prevent another click
      event.target.disabled = true; // disable input, it's enabled and focus is in it
    }

    if (type === 'compress') {
      this.fileProgressIndicator.compressed++;
      this.fileProgressIndicator.compEl.value = parseInt(
        (100 / this.fileProgressIndicator.itemsTotal) *
          this.fileProgressIndicator.compressed,
      );
      if (!success)
        this.fileProgressIndicator.compEl.classList.toggle('error', true);
    } else if (type === 'transfer') {
      this.fileProgressIndicator.transferred++;
      this.fileProgressIndicator.sendEl.value = parseInt(
        (100 / this.fileProgressIndicator.itemsTotal) *
          this.fileProgressIndicator.transferred,
      );
      if (!success)
        this.fileProgressIndicator.sendEl.classList.toggle('error', true);
    }

    // finally, if all transfers are done and callback function is defined, call it.
    if (
      this.fileProgressIndicator.transferred ===
        this.fileProgressIndicator.itemsTotal &&
      typeof this.fileProgressIndicator.callback === 'function'
    )
      this.fileProgressIndicator.callback();
  } // _updateFileProgress

  /**
   * Check if a file is an allowed image type
   * @param {File} file - The file to validate
   * @returns {boolean} true if file is allowed, false otherwise
   */
  _isAllowedImageType(file) {
    // Check MIME type
    if (this.allowedImageTypes.includes(file.type)) {
      return true;
    }

    // Fallback: check file extension (MIME type can be unreliable)
    const fileName = file.name.toLowerCase();
    return this.allowedImageExtensions.some((ext) => fileName.endsWith(ext));
  }

  /**
   * Compress selected images and upload them
   * @param {Event} event - The file input's change event
   *
   * @returns {boolean} false if there are no files, else true
   */
  _compressAndUploadImages(event) {
    if (!event.target.files.length) return false;

    // Filter only allowed image types
    const allFiles = Array.from(event.target.files);
    const validFiles = allFiles.filter((file) =>
      this._isAllowedImageType(file),
    );
    const invalidFiles = allFiles.filter(
      (file) => !this._isAllowedImageType(file),
    );

    // Warn user about skipped files
    if (invalidFiles.length > 0) {
      const skippedNames = invalidFiles.map((f) => f.name).join(', ');
      console.warn(`Skipped invalid file types: ${skippedNames}`);
      alert(
        `Seuraavat tiedostot ohitettiin, koska ne eivät ole sallittuja kuvatiedostotyyppejä: ${skippedNames}`,
      );
    }

    // If no valid files remain, abort
    if (validFiles.length === 0) {
      event.target.value = '';
      return false;
    }

    event.target.disabled = true; // Disable file input. Page reload enables this after all image trasfers.
    const fileList = validFiles;
    const action = event.target.form.action;

    // Array of elements to copy to each individual image upload form. Excude 'callbackUrl' and 'image' inputs.
    const copyFormEls = Array.from(event.target.form.elements).filter(
      (item) => !['callbackUrl', 'image'].includes(item.name),
    );

    // This object is used to update UI. It's callback function is called when all uploads have finished.
    this.fileProgressIndicator = {
      itemsTotal: event.target.files.length,
      compressed: 0,
      transferred: 0,
      callback: () => {
        location.reload();
      }, // what to do after the transfers are completed
    };

    const parent = this;
    parent._updateFileProgress(event); // This 1st call adds the progress element to page

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const fileName = file.name.toLowerCase();
      const isHeic =
        file.type === 'image/heic' ||
        file.type === 'image/heif' ||
        fileName.endsWith('.heic') ||
        fileName.endsWith('.heif');

      if (isHeic) {
        // HEIC/HEIF is not supported by the Canvas API used by compressorjs (except in Safari).
        // Skip compression and upload the original file directly.
        parent._updateFileProgress(event, 'compress', true);

        const formData = new FormData();

        copyFormEls.forEach((item) => {
          formData.append(item.name, item.value);
        });
        formData.append('image', file, file.name);
        formData.append('callbackUrl', 'none');

        if (i === fileList.length - 1) {
          formData.append('allImagesUploaded', true);
        }

        fetch(action, { method: 'POST', body: formData })
          .then(() => {
            parent._updateFileProgress(event, 'transfer', true);
          })
          .catch(function (error) {
            parent._updateFileProgress(event, 'transfer', false);
          });
      } else {
        new Compressor(file, {
          ...this.imageCompressionSettings,
          success(result) {
            parent._updateFileProgress(event, 'compress', true);

            const formData = new FormData();

            // Copy other form inputs to new form. Example: 'referrer' and 'delivery' fields when updating order images.
            copyFormEls.forEach((item) => {
              formData.append(item.name, item.value);
            });
            formData.append('image', result, result.name); // The third parameter is required for server
            formData.append('callbackUrl', 'none'); // 'none' prevents redirection after ajax Post

            // if last image, add a signal to server that all images have been uploaded
            if (i === fileList.length - 1) {
              formData.append('allImagesUploaded', true);
            }

            // Send the compressed image file to server with XMLHttpRequest.
            fetch(action, { method: 'POST', body: formData })
              .then(() => {
                // handle upload success
                parent._updateFileProgress(event, 'transfer', true);
              })
              .catch(function (error) {
                // handle upload error
                parent._updateFileProgress(event, 'transfer', false);
              });
          },
          error(err) {
            // handle image compression error
            parent._updateFileProgress(event, 'compress', false);
            parent._updateFileProgress(event, 'transfer', false); // transfer must also progress in UI
          },
        });
      }
    }
    return true;
  }
}

module.exports = ImageManager;
